// layouts/admin/index.jsx - Enterprise Yellow Theme
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Breadcrumbs,
  Typography,
  Link as MuiLink,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import routes from "routes.js";
import { useDarkMode } from "hooks/useDarkMode";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { checkRouteAccess } from "utils/routePermissions";

export default function Admin() {
  const theme = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentRoute, setCurrentRoute] = useState("Dashboard");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { darkmode, toggleDarkmode, mounted } = useDarkMode();

  // Responsive sidebar handling
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update current route
  useEffect(() => {
    const activeRoute = routes.find(
      (route) =>
        route.layout === "/admin" &&
        (location.pathname === `${route.layout}/${route.path}` ||
          (route.path === "" && location.pathname === route.layout)),
    );

    if (activeRoute) {
      setCurrentRoute(activeRoute.name);
    } else {
      setCurrentRoute("Dashboard");
    }
  }, [location]);

  // Loading simulation
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 150);
    return () => clearTimeout(timer);
  }, [location]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setSidebarOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  }, [isMobile]);

  const closeSidebar = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const role = getSecureLocalItem("role") || "SUPER_ADMIN";

  // Memoized routes for better performance
  const getRoutes = useMemo(() => {
    return routes
      .map((route, index) => {
        if (route.layout === "/admin" && checkRouteAccess(route, role)) {
          return (
            <Route
              key={index}
              path={`/${route.path}`}
              element={
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  style={{ height: "100%" }}
                >
                  {route.component}
                </motion.div>
              }
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  }, [role]);

  // Calculate sidebar width
  const sidebarWidth = useMemo(() => {
    if (!sidebarOpen) return 0;
    if (isMobile) return 280;
    if (sidebarCollapsed) return 80;
    return 280;
  }, [sidebarOpen, sidebarCollapsed, isMobile]);

  // Calculate main content margin
  const mainContentMargin = useMemo(() => {
    if (isMobile || !sidebarOpen) return "0px";
    if (sidebarCollapsed) return "80px";
    return "280px";
  }, [sidebarOpen, sidebarCollapsed, isMobile]);

  if (!mounted) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: "center" }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ position: "relative", width: 80, height: 80, mx: "auto" }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "4px solid",
                  borderColor:
                    theme.palette.mode === "dark" ? "#fcd34d" : "#fbbf24",
                  borderTopColor: "#f59e0b",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                      color: "#ffffff",
                    }}
                  >
                    E
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
            }}
          >
            Loading Dashboard
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.mode === "dark" ? "#6b7280" : "#9ca3af",
            }}
          >
            Please wait...
          </Typography>
        </motion.div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
        transition: "all 0.3s",
      }}
    >
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        onClose={closeSidebar}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        darkmode={darkmode}
        toggleDarkmode={toggleDarkmode}
        isMobile={isMobile}
      />

      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          transition: "all 0.3s ease-in-out",
          marginLeft: mainContentMargin,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        {/* Navbar */}
        <Navbar
          darkmode={darkmode}
          toggleDarkmode={toggleDarkmode}
          onToggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
          isMobile={isMobile}
        />

        {/* Loading Overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(15,23,42,0.9)"
                    : "rgba(255,255,255,0.9)",
                backdropFilter: "blur(4px)",
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                style={{ textAlign: "center" }}
              >
                <CircularProgress
                  size={40}
                  sx={{
                    mb: 2,
                    color: "primary.main",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                  }}
                >
                  Loading Content
                </Typography>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <Box
          component="main"
          sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 } }}
        >
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Box
              sx={{
                mb: 2,
                px: 1,
                py: 0.5,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <Breadcrumbs
                separator={
                  <ChevronRightIcon
                    sx={{
                      fontSize: 18,
                      fontWeight: 500,
                      color:
                        theme.palette.mode === "dark" ? "#8497b1" : "#596474",
                    }}
                  />
                }
              >
                <MuiLink
                  underline="hover"
                  color="inherit"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    gap: 0.5,
                    color:
                      theme.palette.mode === "dark" ? "#8497b1" : "#596474",
                    fontSize: "0.875rem",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <HomeIcon sx={{ fontSize: 16 }} />
                  Home
                </MuiLink>
                <Typography
                  sx={{
                    color: "primary.main",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {currentRoute}
                </Typography>
              </Breadcrumbs>
            </Box>
          </motion.div>

          {/* Page Content */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Routes>
              {getRoutes}
              <Route
                path="/"
                element={<Navigate to="/admin/dashboard" replace />}
              />
            </Routes>
          </motion.div>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            borderTop: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(30,41,59,0.8)"
                : "rgba(255,255,255,0.8)",
            backdropFilter: "blur(24px)",
          }}
        >
          <Box sx={{ px: 3, py: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette.mode === "dark" ? "#6b7280" : "#9ca3af",
                  }}
                >
                  © {new Date().getFullYear()} EBS Office Management System
                </Typography>
              </Box>
              {/* <Box sx={{ display: "flex", gap: 3 }}>
                {["Privacy", "Terms", "Support"].map((item) => (
                  <Typography
                    key={item}
                    variant="caption"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#6b7280" : "#9ca3af",
                      cursor: "pointer",
                      transition: "color 0.2s",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box> */}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
            }}
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>
    </Box>
  );
}
